/**
 * Comprehensive Jobs Management
 * Full implementation of all Jobs API endpoints
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  listJobs,
  getJob,
  cancelJob,
  requeueJob,
  clearJobs,
  submitIngestJob,
  submitParseJob,
  submitIndexJob,
} from '../../api/jobs';
import { listArtifacts } from '../../api/artifacts';
import { useWorkspace } from '../../hooks/useWorkspace';
import { IngestArtifactsModal } from './IngestArtifactsModal';
import { JobExpandedView } from './JobExpandedView';
import { 
  Zap, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  RefreshCw,
  XCircle,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader,
  FileText,
  Database,
  Search as SearchIcon,
} from 'lucide-react';

export function JobList() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  
  // Read state from URL
  const params = new URLSearchParams(window.location.search);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>(params.get('type') || 'all');
  const [filterStatus, setFilterStatus] = useState<string>(params.get('status') || 'all');
  const [sortBy, setSortBy] = useState<'created_at' | 'type' | 'status'>((params.get('sort') as any) || 'created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((params.get('order') as any) || 'desc');
  const [page, setPage] = useState(parseInt(params.get('page') || '0'));
  const [pageSize] = useState(20);
  const [showIngestModal, setShowIngestModal] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  
  // Update URL when filters change
  const updateURL = () => {
    const url = new URL(window.location.href);
    if (filterType !== 'all') url.searchParams.set('type', filterType);
    if (filterStatus !== 'all') url.searchParams.set('status', filterStatus);
    url.searchParams.set('sort', sortBy);
    url.searchParams.set('order', sortOrder);
    if (page > 0) url.searchParams.set('page', String(page));
    window.history.pushState({}, '', url.toString());
  };
  
  React.useEffect(() => {
    updateURL();
  }, [filterType, filterStatus, sortBy, sortOrder, page]);

  const { data: jobs, isLoading, refetch } = useQuery({
    queryKey: ['jobs', projectId],
    queryFn: () => listJobs(projectId!),
    enabled: !!projectId,
    refetchInterval: 5000,
  });

  // Also fetch artifacts list to use in expanded view
  const { data: artifacts } = useQuery({
    queryKey: ['artifacts', projectId],
    queryFn: () => listArtifacts(projectId!),
    enabled: !!projectId,
  });

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
    },
  });

  const requeueMutation = useMutation({
    mutationFn: (jobId: string) => requeueJob(jobId, true), // Force reprocess already-processed files
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: (status?: string) => clearJobs(projectId!, status),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      alert(`Cleared ${result.deleted} job${result.deleted !== 1 ? 's' : ''}`);
    },
  });

  const filteredJobs = React.useMemo(() => {
    if (!jobs) return [];
    
    return jobs.filter((job) => {
      const matchesSearch = 
        job.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.status?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || job.type === filterType;
      const matchesStatus = filterStatus === 'all' || job.status === filterStatus;
      
      return matchesSearch && matchesType && matchesStatus;
    }).sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [jobs, searchTerm, filterType, filterStatus, sortBy, sortOrder]);

  const paginatedJobs = React.useMemo(() => {
    const start = page * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, page, pageSize]);

  const totalPages = Math.ceil(filteredJobs.length / pageSize);

  const runningJobs = jobs?.filter(j => ['pending', 'queued', 'running'].includes(j.status)).length || 0;
  const failedJobs = jobs?.filter(j => j.status === 'failed').length || 0;
  const completedJobs = jobs?.filter(j => j.status === 'completed').length || 0;
  const bm25Jobs = jobs?.filter(j => j.type === 'build_bm25_index').length || 0;
  const pendingBm25Jobs = jobs?.filter(j => j.type === 'build_bm25_index' && ['pending', 'queued', 'running'].includes(j.status)).length || 0;

  const statusVariant = {
    pending: 'warning',
    queued: 'warning',
    running: 'info',
    completed: 'success',
    failed: 'error',
    cancelled: 'default',
  } as const;

  const statusIcons = {
    pending: Clock,
    queued: Clock,
    running: Loader,
    completed: CheckCircle,
    failed: XCircle,
    cancelled: XCircle,
  };

  const typeIcons = {
    ingest: Database,
    parse: FileText,
    index: SearchIcon,
    report_generation: FileText,
    build_bm25_index: SearchIcon,  // BM25 keyword index build
  };

  // Job type labels for better display
  const typeLabels: Record<string, string> = {
    ingest: 'Ingest',
    parse: 'Parse',
    index: 'Index',
    report_generation: 'Report Generation',
    build_bm25_index: 'BM25 Keyword Index',
  };

  const toggleJobExpanded = (jobId: string) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Select a project to view jobs</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Jobs</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
              {runningJobs > 0 && (
                <span className="ml-2 text-blue-600 dark:text-blue-400 font-medium">
                  ({runningJobs} running)
                </span>
              )}
              {failedJobs > 0 && (
                <span className="ml-2 text-red-600 dark:text-red-400 font-medium">
                  ({failedJobs} failed)
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm"
            onClick={() => setShowIngestModal(true)}
          >
            <Database className="h-4 w-4" />
            Ingest Artifacts
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => {
              if (window.confirm(`Clear ${runningJobs} queued/stuck jobs?`)) {
                clearMutation.mutate('queued');
              }
            }}
            disabled={clearMutation.isPending || runningJobs === 0}
            className="text-orange-600 hover:text-orange-700"
          >
            <XCircle className="h-4 w-4" />
            Clear Queued ({runningJobs})
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => {
              if (window.confirm(`Clear ALL ${jobs?.length || 0} jobs?`)) {
                clearMutation.mutate();
              }
            }}
            disabled={clearMutation.isPending || !jobs || jobs.length === 0}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Clear All
          </Button>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Loader className="h-8 w-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{runningJobs}</div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Running</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 dark:from-green-900/20 dark:to-emerald-900/20 dark:border-green-800">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            <div>
              <div className="text-2xl font-bold text-green-900 dark:text-green-100">{completedJobs}</div>
              <div className="text-sm text-green-700 dark:text-green-300">Completed</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-200 dark:from-red-900/20 dark:to-pink-900/20 dark:border-red-800">
          <div className="flex items-center gap-3">
            <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            <div>
              <div className="text-2xl font-bold text-red-900 dark:text-red-100">{failedJobs}</div>
              <div className="text-sm text-red-700 dark:text-red-300">Failed</div>
            </div>
          </div>
        </Card>

        {/* BM25 Index Jobs Card */}
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200 dark:from-indigo-900/20 dark:to-purple-900/20 dark:border-indigo-800">
          <div className="flex items-center gap-3">
            <SearchIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
            <div>
              <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">{bm25Jobs}</div>
              <div className="text-sm text-indigo-700 dark:text-indigo-300">
                BM25 Index
                {pendingBm25Jobs > 0 && (
                  <span className="ml-1 text-amber-600">({pendingBm25Jobs} queued)</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200 dark:from-slate-800 dark:to-slate-700 dark:border-slate-600">
          <div className="flex items-center gap-3">
            <Zap className="h-8 w-8 text-gray-600 dark:text-gray-400" />
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{jobs?.length || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search jobs by type, ID, or status..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Filter className="inline h-4 w-4 mr-1" />
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {Array.from(new Set(jobs?.map(j => j.type).filter(Boolean))).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="queued">Queued</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="created_at">Created Date</option>
                  <option value="type">Type</option>
                  <option value="status">Status</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Jobs List */}
      <div className="space-y-3 w-full">
        {paginatedJobs.map((job) => {
          const StatusIcon = statusIcons[job.status as keyof typeof statusIcons] || Clock;
          const TypeIcon = typeIcons[job.type as keyof typeof typeIcons] || Zap;
          const isRunning = ['pending', 'queued', 'running'].includes(job.status);
          const isExpanded = expandedJobs.has(job.id);
          const artifactIds = job.input_data?.artifact_ids || [];
          const outputData = job.output_data || {};

          return (
            <Card key={job.id} className="hover:shadow-lg transition-shadow w-full">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <TypeIcon className="h-6 w-6 text-blue-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                          {(job as any).name || typeLabels[job.type] || job.type}
                        </h3>
                        <Badge variant={statusVariant[job.status as keyof typeof statusVariant]}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {job.status}
                        </Badge>
                        {artifactIds.length > 0 && (
                          <Badge variant="default" className="text-xs">
                            {artifactIds.length} artifact{artifactIds.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        {(job as any).actual_item_count !== undefined && (job as any).actual_item_count !== null && (
                          <Badge variant="success" className="text-xs">
                            {(job as any).actual_item_count.toLocaleString()} items synced
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {(job as any).source_name && (
                          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium mr-2">
                            {(job as any).source_name}
                          </span>
                        )}
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-mono break-all">{job.id}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(job.id);
                          }}
                          title="Copy Job ID"
                        >
                          <FileText className="h-3 w-3" />
                        </Button>
                      </div>
                      {(job as any).filters_applied && Object.keys((job as any).filters_applied).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          <span className="text-xs text-gray-500">Filters:</span>
                          {Object.entries((job as any).filters_applied).map(([key, value]) => (
                            <Badge key={key} variant="outline" className="text-xs">
                              {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                    <div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Created</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {new Date(job.created_at).toLocaleString()}
                      </div>
                    </div>
                    {job.started_at && (
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Started</div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {new Date(job.started_at).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {job.completed_at && (
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Completed</div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {new Date(job.completed_at).toLocaleString()}
                        </div>
                        {job.started_at && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {(() => {
                              const durationMs = new Date(job.completed_at).getTime() - new Date(job.started_at).getTime();
                              const durationSec = Math.round(durationMs / 1000);
                              if (durationSec === 0 && durationMs > 0) {
                                return `Duration: <1s`;
                              } else if (durationSec === 0) {
                                return `Duration: 0s (check timestamps)`;
                              }
                              return `Duration: ${durationSec}s`;
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    {outputData.num_chunks_created !== undefined && (
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                        <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Chunks Created</div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                          {outputData.num_chunks_created.toLocaleString()}
                        </div>
                        {(() => {
                          // Estimate expected chunks based on artifacts
                          const jobArtifactIds = job.input_data?.artifact_ids || [];
                          const jobArtifacts = artifacts?.filter(a => jobArtifactIds.includes(a.id)) || [];
                          if (jobArtifacts.length > 0 && outputData.num_chunks_created === 0 && job.status === 'completed') {
                            return (
                              <div className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Expected chunks but got 0
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    {outputData.total_entities_created !== undefined && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2">
                        <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Entities Created</div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                          {outputData.total_entities_created.toLocaleString()}
                        </div>
                        {(() => {
                          // Estimate expected entities based on artifacts
                          const jobArtifactIds = job.input_data?.artifact_ids || [];
                          const jobArtifacts = artifacts?.filter(a => jobArtifactIds.includes(a.id)) || [];
                          if (jobArtifacts.length > 0 && outputData.total_entities_created === 0 && job.status === 'completed') {
                            return (
                              <div className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Expected entities but got 0
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    {/* BM25 Index Build Output */}
                    {job.type === 'build_bm25_index' && outputData.entry_count !== undefined && (
                      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-2">
                        <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Entries Indexed</div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                          {outputData.entry_count.toLocaleString()}
                        </div>
                        {outputData.bm25_indexed && (
                          <div className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            BM25 Index Ready
                          </div>
                        )}
                      </div>
                    )}
                    {job.type === 'build_bm25_index' && outputData.elapsed_seconds !== undefined && (
                      <div className="bg-slate-50 dark:bg-slate-900/20 rounded-lg p-2">
                        <div className="text-gray-600 dark:text-gray-400 text-xs mb-1">Build Time</div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                          {outputData.elapsed_seconds}s
                        </div>
                      </div>
                    )}
                  </div>

                  {job.error_message && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-red-900">Error</div>
                          <div className="text-sm text-red-700">{job.error_message}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Enhanced Progress Display with Queue Position & Stage */}
                  {(job.progress_data || job.queue_position || job.current_stage) && (
                    <div className="mt-3 space-y-2">
                      {/* Queue Position for Pending Jobs */}
                      {job.queue_position && ['pending', 'queued'].includes(job.status) && (
                        <div className="flex items-center gap-2 text-sm bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
                          <Clock className="h-4 w-4 text-amber-600" />
                          <span className="text-amber-800 dark:text-amber-200">
                            Queue Position: <span className="font-bold">#{job.queue_position}</span>
                          </span>
                          {job.queue_position === 1 && (
                            <Badge variant="warning" className="ml-2 text-xs">Next up</Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Current Stage & Message */}
                      {(job.current_stage || job.stage_message) && (
                        <div className="flex items-center gap-2 text-sm bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2">
                          {isRunning ? (
                            <Loader className="h-4 w-4 text-blue-600 animate-spin" />
                          ) : (
                            <Zap className="h-4 w-4 text-blue-600" />
                          )}
                          <div className="flex-1">
                            <span className="text-blue-800 dark:text-blue-200 font-medium capitalize">
                              {job.current_stage || 'Processing'}
                            </span>
                            {job.stage_message && (
                              <span className="text-blue-600 dark:text-blue-300 ml-2">
                                — {job.stage_message}
                              </span>
                            )}
                          </div>
                          {job.estimated_time_remaining && job.estimated_time_remaining > 0 && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              ~{Math.ceil(job.estimated_time_remaining / 60)}min remaining
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Progress Bar */}
                      {(job.progress_percentage !== null && job.progress_percentage !== undefined) || (job.progress_data && isRunning) ? (
                        <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600 dark:text-gray-400">Progress</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                              {job.progress_percentage !== null && job.progress_percentage !== undefined
                                ? `${Math.round(job.progress_percentage)}%`
                                : `${Math.round((job.progress_data?.current || 0) / (job.progress_data?.total || 1) * 100)}%`
                              }
                        </span>
                      </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                        <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                job.status === 'completed' ? 'bg-green-500' :
                                job.status === 'failed' ? 'bg-red-500' :
                                'bg-blue-600'
                              }`}
                          style={{ 
                                width: `${
                                  job.progress_percentage !== null && job.progress_percentage !== undefined
                                    ? job.progress_percentage
                                    : (job.progress_data?.current || 0) / (job.progress_data?.total || 1) * 100
                                }%` 
                          }}
                        />
                      </div>
                          {/* Progress Details for BM25 jobs */}
                          {job.type === 'build_bm25_index' && job.progress_data?.items_processed !== undefined && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {job.progress_data.items_processed.toLocaleString()} / {job.progress_data.total_items?.toLocaleString() || '?'} documents indexed
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Expanded View */}
                  {isExpanded && artifactIds.length > 0 && (
                    <JobExpandedView 
                      job={job} 
                      artifactIds={artifactIds} 
                      artifacts={artifacts || []}
                    />
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 ml-4">
                  {artifactIds.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleJobExpanded(job.id)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </Button>
                  )}
                  {isRunning && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cancelMutation.mutate(job.id)}
                      disabled={cancelMutation.isPending}
                      className="text-red-600 hover:text-red-700"
                    >
                      <XCircle className="h-4 w-4" />
                      Cancel
                    </Button>
                  )}
                  {(job.status === 'cancelled' || job.status === 'failed' || job.status === 'completed') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => requeueMutation.mutate(job.id)}
                      disabled={requeueMutation.isPending}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Requeue
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {filteredJobs.length === 0 && !isLoading && (
        <Card>
          <div className="text-center py-12">
            <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No jobs found</p>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, filteredJobs.length)} of {filteredJobs.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                  const pageNum = totalPages <= 10 
                    ? i 
                    : page < 5 
                      ? i 
                      : page > totalPages - 5 
                        ? totalPages - 10 + i 
                        : page - 5 + i;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 rounded ${
                        page === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Ingest Artifacts Modal */}
      <IngestArtifactsModal
        isOpen={showIngestModal}
        onClose={() => setShowIngestModal(false)}
      />
    </div>
  );
}

export default JobList;
